/**
 * Accettazione dell'invito (V04): primo accesso di un account invitato.
 *
 * Mostra societa e ruoli, fa correggere nome e cognome e scegliere la
 * password. Se l'invito non vale piu (scaduto, gia accettato, societa
 * disattivata) la pagina mostra solo il messaggio del server, che dice cosa
 * fare. Attivato l'account la sessione e gia aperta: si entra ricaricando.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { nuovaPasswordSchema } from '@calcio/shared';
import { ErroreApi, chiama } from '../api/client.js';
import { ETICHETTA_RUOLO, type RuoloAccount, nomeSquadra } from '../amministrazione/ruoli.js';
import './accesso.css';

interface Invito {
  societa: string;
  email: string;
  nome: string;
  cognome: string;
  ruoli: {
    ruolo: RuoloAccount;
    squadra: string | null;
    categoria: string | null;
    stagione: string | null;
  }[];
}

type Stato =
  | { fase: 'caricamento' }
  | { fase: 'errore'; messaggio: string }
  | { fase: 'modulo'; invito: Invito };

const messaggioDi = (e: unknown) =>
  e instanceof ErroreApi && e.stato !== 400 && e.stato < 500
    ? e.message
    : 'Invito non valido: controlla di aver aperto il link completo ricevuto via email.';

export function PaginaInvito() {
  const { codice = '' } = useParams();
  const [stato, setStato] = useState<Stato>({ fase: 'caricamento' });
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [password, setPassword] = useState('');
  const [conferma, setConferma] = useState('');
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    chiama<Invito>(`/invito/${codice}`)
      .then((invito) => {
        setNome(invito.nome);
        setCognome(invito.cognome);
        setStato({ fase: 'modulo', invito });
      })
      .catch((e: unknown) => setStato({ fase: 'errore', messaggio: messaggioDi(e) }));
  }, [codice]);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    if (!nome.trim() || !cognome.trim()) return setErrore('Nome e cognome sono obbligatori.');
    const esito = nuovaPasswordSchema.safeParse(password);
    if (!esito.success) return setErrore(esito.error.issues[0]!.message);
    if (password !== conferma) return setErrore('Le due password non coincidono.');
    setInvio(true);
    setErrore(null);
    try {
      await chiama(`/invito/${codice}`, { metodo: 'POST', corpo: { nome, cognome, password } });
      // La sessione e aperta dal server: una ricarica completa fa ripartire
      // l'app da autenticata, anche se in questo browser c'era un altro account.
      window.location.replace('/');
    } catch (err) {
      if (err instanceof ErroreApi && err.stato >= 403 && err.stato < 500) {
        setStato({ fase: 'errore', messaggio: err.message });
      } else {
        setErrore('Attivazione non riuscita. Riprova.');
      }
      setInvio(false);
    }
  };

  return (
    <div className="accesso">
      <form className="accesso-scheda" onSubmit={(e) => void invia(e)}>
        <h1>Attiva l'account</h1>

        {stato.fase === 'caricamento' && <p className="accesso-nota">Controllo l'invito…</p>}

        {stato.fase === 'errore' && (
          <>
            <p>{stato.messaggio}</p>
            <Link to="/accesso" className="accesso-link">
              Vai all'accesso
            </Link>
          </>
        )}

        {stato.fase === 'modulo' && (
          <>
            <p>
              Invito in <strong>{stato.invito.societa}</strong> per l'email{' '}
              <strong>{stato.invito.email}</strong>, con questi ruoli:
            </p>
            {stato.invito.ruoli.length > 0 && (
              <ul className="accesso-ruoli">
                {stato.invito.ruoli.map((r, i) => (
                  <li key={i}>
                    {ETICHETTA_RUOLO[r.ruolo]}
                    {r.squadra && ` · ${nomeSquadra(r)} ${r.stagione ?? ''}`}
                  </li>
                ))}
              </ul>
            )}

            {errore && (
              <div className="banda" role="alert">
                {errore}
              </div>
            )}

            <label className="campo">
              Nome
              <input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                autoComplete="given-name"
                required
              />
            </label>

            <label className="campo">
              Cognome
              <input
                value={cognome}
                onChange={(e) => setCognome(e.target.value)}
                autoComplete="family-name"
                required
              />
            </label>

            <label className="campo">
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
            </label>

            <label className="campo">
              Ripeti la password
              <input
                type="password"
                value={conferma}
                onChange={(e) => setConferma(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>

            <p className="accesso-nota">
              Almeno 10 caratteri. Con l'email e questa password accederai d'ora in poi.
            </p>

            <button type="submit" disabled={invio}>
              {invio ? 'Attivazione…' : "Attiva l'account"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
